import { createReducer, on } from '@ngrx/store';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

import { ResetBundles, SelectBundle } from '../actions';
import * as manageAppsActions from '../actions/manage-apps.actions';
import * as sessionActions from '../actions/session.actions';
import { manageAppsAdapters, ManageAppsOrganizationEntity, manageAppsState, Service } from '../states/manage-apps.state';

export const MANAGE_APPS_REDUCER_KEY = 'manageApps';
export const MANAGE_APPS_REDUCER = createReducer(
    manageAppsState,
    on(sessionActions.SetCurrentOrganization, (state, action) => {
        return {
            ...state,
            currentOrganizationId: action.organization?.id,
        };
    }),
    on(manageAppsActions.ManageAppsSetOrganization, (state, action) => {
        return {
            ...state,
            currentOrganizationId: action.organization?.id,
        };
    }),
    on(manageAppsActions.ManageAppsSetOrgsAndApps, (state, action) => {
        return {
            ...state,
            pageIndex: 0,
            orgIdArray: action.orgIdArray,
            itemIdArray: action.itemIdArray,
            itemNames: action.itemNames,
        };
    }),
    on(manageAppsActions.ManageAppsChangeCategory, (state, action) => {
        return {
            ...state,
            pageIndex: 0,
            assetType: action.assetType,
        };
    }),
    on(manageAppsActions.SetManageAppsPageIndex, (state, action) => {
        return {
            ...state,
            pageIndex: action.pageIndex,
        };
    }),
    on(manageAppsActions.SetManageAppsPageSize, (state, action) => {
        return {
            ...state,
            pageIndex: 0,
            pageSize: action.size,
        };
    }),
    on(manageAppsActions.SetManageAppsOrganizationEntity, (state, action) => {
        return {
            ...state,
            organizations: action.organizationsAdapter.addOne(action.servicesEntity, state.organizations),
        };
    }),
    on(manageAppsActions.SetManageAppsStatus, (state, action) => {
        return {
            ...state,
            subscriptionStatus: action.subscriptionStatus,
            pageIndex: 0,
            pageSize: state.pageSize,
            assetType: undefined,
            searchText: undefined,
            currentBridgeId: undefined,
            selectedServiceIds: state.selectedBundleId || state.selectedPackageId ? state.selectedServiceIds : [],
            itemIdArray: [],
            itemNames: [],
            orgIdArray: [],
        };
    }),
    on(manageAppsActions.SetManageAppsSearchText, (state, action) => {
        return {
            ...state,
            searchText: action.searchText?.toLowerCase(),
            pageIndex: 0,
        };
    }),

    on(manageAppsActions.SetManageAppsResponse, (state, action) => {
        const organizationEntity: ManageAppsOrganizationEntity = state.organizations.entities[state.currentOrganizationId];
        return {
            ...state,
            organizations: action.organizationsAdapter.updateOne(
                {
                    id: state.currentOrganizationId,
                    changes: {
                        orgIdVsName: {
                            ...(organizationEntity.orgIdVsName || {}),
                            ...(action.orgIdVsName || {}),
                        },
                        services: action.servicesAdapter.addMany(action.services, organizationEntity.services),
                    },
                },
                state.organizations
            ),
        };
    }),
    on(manageAppsActions.UpdateManageAppsService, (state, action) => {
        const organizationEntity: ManageAppsOrganizationEntity = state.organizations.entities[state.currentOrganizationId];
        return {
            ...state,
            organizations: action.organizationsAdapter.updateOne(
                {
                    id: state.currentOrganizationId,
                    changes: {
                        services: action.servicesAdapter.updateOne(
                            {
                                id: action.assetId,
                                changes: {
                                    [action.property]: action.propertyValue,
                                },
                            },
                            organizationEntity.services
                        ),
                    },
                },
                state.organizations
            ),
        };
    }),
    on(manageAppsActions.ManageAppsResetSearchText, state => {
        return {
            ...state,
            searchText: undefined,
        };
    }),
    on(manageAppsActions.ManageAppsSetBridgeId, (state, action) => {
        return {
            ...state,
            currentBridgeId: action.assetId,
        };
    }),
    on(manageAppsActions.ManageAppsUpdateMany, (state, action) => {
        const organizationEntity: ManageAppsOrganizationEntity = state.organizations.entities[state.currentOrganizationId];
        return {
            ...state,
            organizations: action.organizationsAdapter.updateOne(
                {
                    id: state.currentOrganizationId,
                    changes: {
                        services: action.servicesAdapter.updateMany(
                            action.ids.map(id =>
                                Object.assign(
                                    {},
                                    {
                                        id: id,
                                        changes: {
                                            [action.property]: action.propertyValue,
                                            status: 'PENDING' as any,
                                        },
                                    }
                                )
                            ),
                            organizationEntity.services
                        ),
                    },
                },
                state.organizations
            ),
        };
    }),
    on(SelectBundle, (state, action) => {
        return {
            ...state,
            selectedBundleId: action.id,
        };
    }),
    on(manageAppsActions.SetSelectedPackageId, (state, action) => {
        return {
            ...state,
            selectedPackageId: action.packageId,
        };
    }),
    on(manageAppsActions.SetManageAppsAssignableSubscriptionIds, (state, action) => {
        return {
            ...state,
            assignableSubscriptionIds: {
                ...state.assignableSubscriptionIds,
                [state.currentOrganizationId]: action.ids,
            },
        };
    }),
    on(manageAppsActions.SetManageAppsBundleSubscriptionIds, (state, action) => {
        return {
            ...state,
            bundleSubscribedAppIds: {
                ...state.bundleSubscribedAppIds,
                [state.selectedBundleId]: action.ids,
            },
        };
    }),
    on(ResetBundles, state => {
        return {
            ...state,
            selectedBundleId: undefined,
            selectedServiceIds: [],
        };
    }),
    on(manageAppsActions.ManageAppsSetCount, (state, action) => {
        const orgIdString = state.orgIdArray.join(',');
        const itemIdString = state.itemIdArray.join(',');
        return {
            ...state,
            count: {
                ...state.count,
                [`${state.subscriptionStatus}|${orgIdString}|${itemIdString}`]: action.count,
            },
        };
    }),
    on(manageAppsActions.ClearManageAppsSubscriptionApproval, state => {
        const servicesAdapter = manageAppsAdapters[state.currentOrganizationId];
        if (!state.organizations.entities[state.currentOrganizationId]) {
            return state;
        }
        return {
            ...state,
            subscriptionStatus: 'ALL' as 'ALL',
            pageIndex: 0,
            pageSize: 20,
            itemIdArray: [],
            orgIdArray: [],
            itemNames: [],
            organizations: manageAppsAdapters.organizations.updateOne(
                {
                    id: state.currentOrganizationId,
                    changes: {
                        orgIdVsName: {},
                        services: servicesAdapter.getInitialState(),
                    },
                },
                state.organizations
            ),
        };
    }),
    on(manageAppsActions.ManageAppsSetGetByPagination, (state, action) => {
        return {
            ...state,
            getAppsByPagination: action.status,
            pageSize: 10,
            count: {
                APPROVED: 0,
                PENDING: 0,
                PENDING_DEACTIVATE: 0,
            },
        };
    }),
    on(manageAppsActions.ManageAppsUpdateProperty, (state, action) => {
        const organizationEntity: ManageAppsOrganizationEntity = state.organizations.entities[state.currentOrganizationId];
        return {
            ...state,
            organizations: action.organizationsAdapter.updateOne(
                {
                    id: state.currentOrganizationId,
                    changes: {
                        services: action.servicesAdapter.updateMany(
                            action.ids.map(id =>
                                Object.assign(
                                    {},
                                    {
                                        id: id,
                                        changes: {
                                            [action.property]: action.propertyValue,
                                        },
                                    }
                                )
                            ),
                            organizationEntity.services
                        ),
                    },
                },
                state.organizations
            ),
        };
    }),
    on(manageAppsActions.SetAllApps, (state, action) => {
        if (action.allApps) {
            return state;
        }
        const isGlobal = action.isGlobalRepository ?? false;
        return {
            ...state,
            isGlobalRepository: isGlobal,
            organizationsMap: {
                ...state.organizationsMap,
                [action.organizationId]: {
                    ...state.organizationsMap?.[action.organizationId],
                    nonSubscribedApps: action.services?.map(service => ({
                        ...service,
                        selected: state.selectedServiceIds.includes(service.serviceId),
                        isGlobalRepository: isGlobal,
                    })),
                },
            },
        };
    }),
    on(manageAppsActions.SetAllApps, (state, action) => {
        if (!action.allApps) {
            return state;
        }
        const isGlobal = action.isGlobalRepository ?? false;
        return {
            ...state,
            isGlobalRepository: isGlobal,
            organizationsMap: {
                ...state.organizationsMap,
                [action.organizationId]: {
                    ...state.organizationsMap?.[action.organizationId],
                    allApps: action.services?.map(service => ({
                        ...service,
                        selected: state.selectedServiceIds.includes(service.serviceId),
                        isGlobalRepository: isGlobal,
                    })),
                },
            },
        };
    }),
    on(manageAppsActions.ClearManageAppsData, (state, action) => {
        return {
            ...state,
            organizationsMap: {
                ...state?.organizationsMap,
                [action.organizationId]: {
                    ...state?.organizationsMap?.[action?.organizationId],
                    nonSubscribedApps: [],
                },
            },
        };
    }),

    on(manageAppsActions.SetAppsByStatus, (state, action) => {
        return {
            ...state,
            organizationsMap: {
                ...state?.organizationsMap,
                [action.organizationId]: {
                    ...state?.organizationsMap?.[action?.organizationId],
                    appsByStatus: {
                        ...state?.organizationsMap?.[action.organizationId]?.appsByStatus,
                        [action.status]: action.services,
                    },
                },
            },
        };
    }),
    on(manageAppsActions.SetSubscriptionAppsByStatus, (state, action) => {
        const storedServices = action.resetSubscriptions
            ? []
            : CommonUtilsService.cloneObject(
                  state.organizationsMap?.[state?.currentOrganizationId]?.subscriptionApprovalApps?.[action.status]
              );
        const combinedServices = [...(storedServices || []), ...action.services];
        const updatedServices = combinedServices.filter(
            (service, index, self) =>
                index === self.findIndex(s => s.serviceId === service.serviceId && s.organizationId === service.organizationId)
        );
        return {
            ...state,
            organizationsMap: {
                ...state?.organizationsMap,
                [action.organizationId]: {
                    ...state?.organizationsMap?.[action?.organizationId],
                    subscriptionApprovalApps: {
                        ...state?.organizationsMap?.[action.organizationId]?.subscriptionApprovalApps,
                        [action.status]: updatedServices,
                    },
                },
            },
        };
    }),
    on(manageAppsActions.SetManageAppsSelectedServiceIds, (state, action) => {
        const services = CommonUtilsService.cloneObject(state.organizationsMap?.[state?.currentOrganizationId]?.nonSubscribedApps);
        const updatedServices = services?.map(service => ({
            ...service,
            selected: action.ids.includes(service.serviceId),
        }));
        return {
            ...state,
            selectedServiceIds: action.ids,
            organizationsMap: {
                ...state?.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state?.organizationsMap?.[state?.currentOrganizationId],
                    nonSubscribedApps: updatedServices,
                },
            },
        };
    }),

    on(manageAppsActions.UpdateSubscribedServices, (state, action) => {
        const services = CommonUtilsService.cloneObject(state.organizationsMap?.[state?.currentOrganizationId]?.nonSubscribedApps);
        const pendingServices = CommonUtilsService.cloneObject(
            state.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus?.['PENDING']
        );
        const subscribedServices = services.filter(
            service => action.ids.includes(service.serviceId) || action.ids.includes(service.restApiName)
        );
        const updatedPendingSevices = pendingServices?.length > 0 ? [...pendingServices, ...subscribedServices] : [];
        const updatedServices = services.filter(
            service => !action.ids.includes(service.serviceId || !action.ids.includes(service.restApiName))
        );
        return {
            ...state,
            selectedServiceIds: action.ids,
            organizationsMap: {
                ...state?.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state?.organizationsMap?.[state?.currentOrganizationId],
                    nonSubscribedApps: updatedServices,
                    appsByStatus: {
                        ['PENDING']: updatedPendingSevices,
                    },
                },
            },
        };
    }),
    on(manageAppsActions.SetSubscriptionPagination, (state, action) => {
        return {
            ...state,
            subscriptionPagination: {
                pageSize: action.pageSize,
                pageIndex: action.pageIndex,
            },
        };
    }),
    on(manageAppsActions.SetOrgIdsAndServiceIds, (state, action) => {
        return {
            ...state,
            organizationIdsArray: action.orgIdArray,
            serviceIdsArray: action.serviceIdArray,
        };
    }),
    on(manageAppsActions.UpdateAssetDetails, (state, action) => {
        const services = CommonUtilsService.cloneObject(state.organizationsMap?.[state?.currentOrganizationId]?.nonSubscribedApps);
        const servicesByStatus = CommonUtilsService.cloneObject(
            state.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus?.[state?.subscriptionStatus]
        );
        let updatedServices;
        let updatedServicesByStatus;

        if (state.subscriptionStatus === 'ALL') {
            updatedServices = services?.map(service =>
                service.serviceId === action.serviceId ? { ...service, [action.property]: action.propertyValue } : service
            );
            return {
                ...state,
                organizationsMap: {
                    ...state?.organizationsMap,
                    [state.currentOrganizationId]: {
                        ...state?.organizationsMap?.[state?.currentOrganizationId],
                        nonSubscribedApps: updatedServices,
                    },
                },
            };
        } else {
            updatedServicesByStatus = servicesByStatus?.map(service =>
                service.serviceId === action.serviceId ? { ...service, [action.property]: action.propertyValue } : service
            );
            return {
                ...state,
                organizationsMap: {
                    ...state?.organizationsMap,
                    [state.currentOrganizationId]: {
                        ...state?.organizationsMap?.[state?.currentOrganizationId],
                        appsByStatus: {
                            ...state?.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus,
                            [state.subscriptionStatus]: updatedServicesByStatus,
                        },
                    },
                },
            };
        }
    }),

    on(manageAppsActions.UpdateManageAppsBridgeNode, (state, action) => {
        const services = CommonUtilsService.cloneObject(state.organizationsMap?.[state?.currentOrganizationId]?.nonSubscribedApps);
        const servicesByStatus = CommonUtilsService.cloneObject(
            state.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus?.[state?.subscriptionStatus]
        );
        let updatedServices;
        let updatedServicesByStatus;

        if (state.subscriptionStatus === 'ALL') {
            updatedServices = services?.map(service =>
                service.serviceId === action.serviceId
                    ? { ...service?.bridgeNodes?.[action.bridgeNodeId], [action.property]: action.propertyValue }
                    : service
            );
            return {
                ...state,
                organizationsMap: {
                    ...state?.organizationsMap,
                    [state.currentOrganizationId]: {
                        ...state?.organizationsMap?.[state?.currentOrganizationId],
                        nonSubscribedApps: updatedServices,
                    },
                },
            };
        } else {
            updatedServicesByStatus = servicesByStatus?.map(service =>
                service.serviceId === action.serviceId
                    ? { ...service?.bridgeNodes?.[action.bridgeNodeId], [action.property]: action.propertyValue }
                    : service
            );
            return {
                ...state,
                organizationsMap: {
                    ...state?.organizationsMap,
                    [state.currentOrganizationId]: {
                        ...state?.organizationsMap?.[state?.currentOrganizationId],
                        appsByStatus: {
                            ...state?.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus,
                            [state.subscriptionStatus]: updatedServicesByStatus,
                        },
                    },
                },
            };
        }
    }),
    on(manageAppsActions.SetSelectedBridgeId, (state, action) => {
        return {
            ...state,
            selectedBridgeId: action.serviceId,
        };
    }),
    on(manageAppsActions.SetBridgeServiceIdVsAssetId, (state, action) => {
        return {
            ...state,
            bridgeIdsVsMetadataIds: {
                ...state.bridgeIdsVsMetadataIds,
                [action.serviceId]: action.assetId,
            },
        };
    }),
    on(manageAppsActions.UpdateServicesWithBridgeNodes, (state, action) => {
        const services = CommonUtilsService.cloneObject(state.organizationsMap?.[state?.currentOrganizationId]?.nonSubscribedApps);
        const servicesByStatus = CommonUtilsService.cloneObject(
            state.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus?.[state?.subscriptionStatus]
        );
        let updatedServices;
        let updatedServicesByStatus;

        if (state.subscriptionStatus === 'ALL') {
            updatedServices = services?.map(service =>
                service.serviceId === action.serviceId ? { ...service, bridgeNodes: action.bridgeNodes } : service
            );
            return {
                ...state,
                organizationsMap: {
                    ...state?.organizationsMap,
                    [state.currentOrganizationId]: {
                        ...state?.organizationsMap?.[state?.currentOrganizationId],
                        nonSubscribedApps: updatedServices,
                    },
                },
            };
        } else {
            updatedServicesByStatus = servicesByStatus?.map(service =>
                service.serviceId === action.serviceId ? { ...service, bridgeNodes: action.bridgeNodes } : service
            );
            return {
                ...state,
                organizationsMap: {
                    ...state?.organizationsMap,
                    [state.currentOrganizationId]: {
                        ...state?.organizationsMap?.[state?.currentOrganizationId],
                        appsByStatus: {
                            ...state?.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus,
                            [state.subscriptionStatus]: updatedServicesByStatus,
                        },
                    },
                },
            };
        }
    }),

    on(manageAppsActions.setSubscriptionsCount, (state, action) => {
        return {
            ...state,
            subscriptionsCount: {
                ...state.subscriptionsCount,
                [action.status]: action.count,
            },
        };
    }),
    on(manageAppsActions.UnsubscribeFromAppSuccess, (state, { subscriptionId }) => {
        return {
            ...state,
            organizationsMap: {
                ...state?.organizationsMap,
                [state.currentOrganizationId]: {
                    ...state?.organizationsMap?.[state?.currentOrganizationId],
                    nonSubscribedApps: [],
                    appsByStatus: Object.keys(state?.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus || {}).reduce(
                        (acc, status) => {
                            acc[status] =
                                state?.organizationsMap?.[state?.currentOrganizationId]?.appsByStatus[status]?.filter(
                                    service => service.subscriptionId !== subscriptionId
                                ) || [];
                            return acc;
                        },
                        {} as { [key: string]: Service[] }
                    ),
                },
            },
        };
    }),
    on(sessionActions.SessionLogoutSuccessfull, () => manageAppsState)
);
